// src/app/pages/admin/TreasuryDashboard.jsx
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Wallet,
  RefreshCw,
  Pencil,
  Search,
  Filter,
  Download,
  CheckCircle,
  XCircle,
  ShieldAlert,
} from "lucide-react";

// Import useAuthSession to check user role
import { useAuthSession } from "../../features/auth/useAuth";

// API functions
import {
  getTreasuryBalances,
  getUserBalances,
  getBalanceSummary,
  adjustUserBalance,
  getPendingWithdrawals,
  approveWithdrawal,
  rejectWithdrawal,
} from "@app/features/admin/walletApi";

// Format currency values
function format(n, d = 2) {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) {
    return (0).toLocaleString(undefined, {
      maximumFractionDigits: d,
      minimumFractionDigits: d,
    });
  }
  return num.toLocaleString(undefined, {
    maximumFractionDigits: d,
    minimumFractionDigits: d,
  });
}

// Determine decimal places based on asset
function getDecimals(asset) {
  if (asset === "BTC") return 8;
  if (asset === "ETH") return 6;
  return 2;
}

// Modal component for adjusting balances and approving/rejecting withdrawals
function Modal({ title, open, onClose, children }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
      <div className="bg-gray-900 w-full max-w-md rounded-xl border border-gray-800 shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-white">{title}</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white rounded-lg"
            type="button"
          >
            <XCircle size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Tabs for the dashboard
function Tabs({ activeTab, setActiveTab }) {
  return (
    <div className="flex border-b border-gray-800">
      <button
        className={`px-4 py-2 text-sm font-medium ${
          activeTab === "treasury"
            ? "text-white border-b-2 border-primary-500"
            : "text-gray-400 hover:text-white"
        }`}
        onClick={() => setActiveTab("treasury")}
        type="button"
      >
        Treasury Balances
      </button>
      <button
        className={`px-4 py-2 text-sm font-medium ${
          activeTab === "users"
            ? "text-white border-b-2 border-primary-500"
            : "text-gray-400 hover:text-white"
        }`}
        onClick={() => setActiveTab("users")}
        type="button"
      >
        User Balances
      </button>
      <button
        className={`px-4 py-2 text-sm font-medium ${
          activeTab === "withdrawals"
            ? "text-white border-b-2 border-primary-500"
            : "text-gray-400 hover:text-white"
        }`}
        onClick={() => setActiveTab("withdrawals")}
        type="button"
      >
        Withdrawal Requests
      </button>
      <button
        className={`px-4 py-2 text-sm font-medium ${
          activeTab === "summary"
            ? "text-white border-b-2 border-primary-500"
            : "text-gray-400 hover:text-white"
        }`}
        onClick={() => setActiveTab("summary")}
        type="button"
      >
        Summary
      </button>
    </div>
  );
}

export function TreasuryDashboard() {
  const queryClient = useQueryClient();
  const { user } = useAuthSession();

  const isAdmin = user?.role?.toUpperCase() === "ADMIN";
  const isModerator = user?.role?.toUpperCase() === "MODERATOR";
  const hasAccess = isAdmin || isModerator;

  if (!hasAccess) {
    return (
      <div className="p-6 bg-gray-950 min-h-screen">
        <div className="bg-gray-900 rounded-xl border border-red-800 p-8 text-center max-w-md mx-auto mt-16">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 rounded-full bg-red-900/30 flex items-center justify-center">
              <ShieldAlert size={32} className="text-red-500" />
            </div>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-gray-400 mb-4">
            You don't have permission to access the Treasury Dashboard. This area
            is restricted to administrators and moderators.
          </p>
          <a
            href="/dashboard"
            className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 inline-block"
          >
            Return to Dashboard
          </a>
        </div>
      </div>
    );
  }

  const [activeTab, setActiveTab] = useState("treasury");

  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);

  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [approveNote, setApproveNote] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [broadcastNow, setBroadcastNow] = useState(false);

  const [assetFilter, setAssetFilter] = useState("");
  const [networkFilter, setNetworkFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [page, setPage] = useState(0);
  const limit = 50;

  const {
    data: treasuryData,
    isLoading: loadingTreasury,
    refetch: refetchTreasury,
  } = useQuery({
    queryKey: ["adminTreasuryBalances"],
    queryFn: getTreasuryBalances,
    staleTime: 30000,
  });

  const {
    data: userBalancesData,
    isLoading: loadingUserBalances,
    refetch: refetchUserBalances,
  } = useQuery({
    queryKey: [
      "adminUserBalances",
      assetFilter,
      networkFilter,
      emailFilter,
      page,
      limit,
    ],
    queryFn: () =>
      getUserBalances({
        asset: assetFilter,
        network: networkFilter,
        email: emailFilter,
        offset: page * limit,
        limit,
      }),
    staleTime: 15000,
  });

  const {
    data: withdrawalsData,
    isLoading: loadingWithdrawals,
    refetch: refetchWithdrawals,
  } = useQuery({
    queryKey: ["adminPendingWithdrawals"],
    queryFn: () => getPendingWithdrawals(),
    staleTime: 15000,
    enabled: activeTab === "withdrawals",
  });

  const {
    data: summaryData,
    isLoading: loadingSummary,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: ["adminBalanceSummary"],
    queryFn: getBalanceSummary,
    staleTime: 30000,
    enabled: activeTab === "summary",
  });

  const adjustBalanceMutation = useMutation({
    mutationFn: adjustUserBalance,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminUserBalances"] });
      queryClient.invalidateQueries({ queryKey: ["adminBalanceSummary"] });
      setAdjustModalOpen(false);
      setSelectedUser(null);
      setAdjustAmount("");
      setAdjustReason("");
    },
  });

  const approveWithdrawalMutation = useMutation({
    mutationFn: (withdrawalId) =>
      approveWithdrawal(withdrawalId, { note: approveNote, broadcastNow }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminPendingWithdrawals"] });
      setApproveModalOpen(false);
      setSelectedWithdrawal(null);
      setApproveNote("");
      setBroadcastNow(false);
    },
  });

  const rejectWithdrawalMutation = useMutation({
    mutationFn: (withdrawalId) =>
      rejectWithdrawal(withdrawalId, { reason: rejectReason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminPendingWithdrawals"] });
      queryClient.invalidateQueries({ queryKey: ["adminUserBalances"] });
      setRejectModalOpen(false);
      setSelectedWithdrawal(null);
      setRejectReason("");
    },
  });

  const handleRefresh = async () => {
    await Promise.all([
      refetchTreasury(),
      refetchUserBalances(),
      activeTab === "withdrawals" ? refetchWithdrawals() : Promise.resolve(),
      activeTab === "summary" ? refetchSummary() : Promise.resolve(),
    ]);
  };

  const exportToCSV = () => {
    if (!userBalancesData?.items) return;

    const headers = [
      "User ID",
      "Email",
      "Asset",
      "Network",
      "Available",
      "Locked",
      "Total Value (USD)",
    ];
    const csvRows = [headers.join(",")];

    userBalancesData.items.forEach((item) => {
      const row = [
        item.userId,
        item.email,
        item.asset,
        item.network,
        item.available,
        item.locked,
        Number(item.totalValue || 0).toFixed(2),
      ];
      csvRows.push(row.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `user-balances-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openAdjustModal = (userRow) => {
    setSelectedUser(userRow);
    setAdjustModalOpen(true);
  };

  const openApproveModal = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setApproveModalOpen(true);
  };

  const openRejectModal = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setRejectModalOpen(true);
  };

  const handleAdjustBalance = (e) => {
    e.preventDefault();
    if (!selectedUser || !adjustAmount || !adjustReason) return;

    adjustBalanceMutation.mutate({
      userId: selectedUser.userId,
      asset: selectedUser.asset,
      network: selectedUser.network,
      amount: parseFloat(adjustAmount),
      reason: adjustReason,
    });
  };

  const handleApproveWithdrawal = (e) => {
    e.preventDefault();
    if (!selectedWithdrawal) return;
    approveWithdrawalMutation.mutate(selectedWithdrawal.id);
  };

  const handleRejectWithdrawal = (e) => {
    e.preventDefault();
    if (!selectedWithdrawal || !rejectReason) return;
    rejectWithdrawalMutation.mutate(selectedWithdrawal.id);
  };

  return (
    <div className="p-6 bg-gray-950 min-h-screen">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 grid place-items-center text-white">
            <Wallet size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Treasury Dashboard</h1>
            <p className="text-sm text-gray-400">
              Monitor and manage DFNS treasury wallets
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            className="px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 text-sm flex items-center gap-2"
            disabled={loadingTreasury || loadingUserBalances}
            type="button"
          >
            <RefreshCw
              size={16}
              className={loadingTreasury || loadingUserBalances ? "animate-spin" : ""}
            />
            Refresh
          </button>

          {activeTab === "users" && (
            <button
              onClick={exportToCSV}
              className="px-3 py-2 rounded-lg bg-indigo-800 hover:bg-indigo-700 border border-indigo-700 text-white text-sm flex items-center gap-2"
              disabled={loadingUserBalances || !userBalancesData?.items?.length}
              type="button"
            >
              <Download size={16} />
              Export CSV
            </button>
          )}
        </div>
      </div>

      <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />

      <div className="mt-6">
        {/* Treasury tab */}
        {activeTab === "treasury" && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
              <h2 className="font-semibold text-white">DFNS Treasury Wallets</h2>
              <div className="text-lg font-bold text-white">
                Total:{" "}
                {treasuryData?.totalUsdValue
                  ? `$${format(treasuryData.totalUsdValue)}`
                  : "—"}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="bg-gray-800/50">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">
                      Asset
                    </th>
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">
                      Network
                    </th>
                    <th className="text-right py-3 px-4 text-sm text-gray-400 font-medium">
                      Balance
                    </th>
                    <th className="text-right py-3 px-4 text-sm text-gray-400 font-medium">
                      USD Value
                    </th>
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">
                      Wallet ID
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTreasury ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-gray-500">
                        <RefreshCw
                          size={24}
                          className="inline-block animate-spin mr-2"
                        />
                        Loading treasury balances...
                      </td>
                    </tr>
                  ) : treasuryData?.balances?.length ? (
                    treasuryData.balances.map((balance, idx) => (
                      <tr
                        key={idx}
                        className="border-t border-gray-800/50 hover:bg-gray-800/20"
                      >
                        <td className="py-4 px-4 font-semibold text-white">
                          {balance.asset}
                        </td>
                        <td className="py-4 px-4 text-gray-300">{balance.network}</td>
                        <td className="py-4 px-4 text-right font-mono text-white">
                          {format(balance.balance, getDecimals(balance.asset))}{" "}
                          {balance.asset}
                        </td>
                        <td className="py-4 px-4 text-right text-green-400">
                          {balance.usdEquivalent
                            ? `$${format(balance.usdEquivalent)}`
                            : "—"}
                        </td>
                        <td className="py-4 px-4 text-gray-500 text-xs font-mono">
                          {balance.walletId
                            ? `${balance.walletId.substring(
                                0,
                                8
                              )}...${balance.walletId.substring(
                                balance.walletId.length - 8
                              )}`
                            : "—"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-gray-500">
                        No treasury data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="px-6 py-4 border-t border-gray-800 text-sm text-gray-500">
              Treasury wallet balances are managed through the DFNS platform and
              synchronized here.
            </div>
          </div>
        )}

        {/* Users tab */}
        {activeTab === "users" && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-800">
              <h2 className="font-semibold text-white mb-4">User Wallet Balances</h2>

              <div className="flex flex-wrap gap-3">
                <div className="flex-1 min-w-[240px]">
                  <div className="relative">
                    <Search
                      size={16}
                      className="absolute left-3 top-[10px] text-gray-500"
                    />
                    <input
                      type="text"
                      value={emailFilter}
                      onChange={(e) => setEmailFilter(e.target.value)}
                      placeholder="Search by email..."
                      className="w-full pl-9 pr-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700 text-white text-sm"
                    />
                  </div>
                </div>

                <select
                  value={assetFilter}
                  onChange={(e) => setAssetFilter(e.target.value)}
                  className="px-3 py-2 rounded-lg bg-gray-800/50 border border-gray-700 text-white text-sm"
                >
                  <option value="">All Assets</option>
                  <option value="BTC">BTC</option>
                  <option value="ETH">ETH</option>
                  <option value="USDT">USDT</option>
                </select>

                <select
                  value={networkFilter}
                  onChange={(e) => setNetworkFilter(e.target.value)}
                  className="px-3 py-2 rounded-lg bg-gray-800/50 border border-gray-700 text-white text-sm"
                >
                  <option value="">All Networks</option>
                  <option value="MAINNET">MAINNET</option>
                  <option value="TRON">TRON</option>
                </select>

                <button
                  onClick={() => {
                    setAssetFilter("");
                    setNetworkFilter("");
                    setEmailFilter("");
                    setPage(0);
                  }}
                  className="px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 text-sm flex items-center gap-2"
                  type="button"
                >
                  <Filter size={16} />
                  Clear Filters
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-gray-800/50">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">
                      User
                    </th>
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">
                      Asset
                    </th>
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">
                      Network
                    </th>
                    <th className="text-right py-3 px-4 text-sm text-gray-400 font-medium">
                      Available
                    </th>
                    <th className="text-right py-3 px-4 text-sm text-gray-400 font-medium">
                      Locked
                    </th>
                    <th className="text-right py-3 px-4 text-sm text-gray-400 font-medium">
                      USD Value
                    </th>
                    <th className="text-center py-3 px-4 text-sm text-gray-400 font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loadingUserBalances ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-gray-500">
                        <RefreshCw
                          size={24}
                          className="inline-block animate-spin mr-2"
                        />
                        Loading user balances...
                      </td>
                    </tr>
                  ) : userBalancesData?.items?.length ? (
                    userBalancesData.items.map((item, idx) => (
                      <tr
                        key={idx}
                        className="border-t border-gray-800/50 hover:bg-gray-800/20"
                      >
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium text-white">
                            {item.email}
                          </div>
                          <div className="text-xs text-gray-500 font-mono">
                            {item.userId}
                          </div>
                        </td>
                        <td className="py-3 px-4 font-medium text-white">
                          {item.asset}
                        </td>
                        <td className="py-3 px-4 text-gray-300">{item.network}</td>
                        <td className="py-3 px-4 text-right font-mono text-white">
                          {format(item.available, getDecimals(item.asset))}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-yellow-400">
                          {format(item.locked, getDecimals(item.asset))}
                        </td>
                        <td className="py-3 px-4 text-right text-green-400">
                          ${format(item.totalValue, 2)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => openAdjustModal(item)}
                            className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 hover:text-white"
                            title="Adjust Balance"
                            type="button"
                          >
                            <Pencil size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-gray-500">
                        No user balances found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {userBalancesData && userBalancesData.total > 0 && (
              <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {page * limit + 1}-
                  {Math.min((page + 1) * limit, userBalancesData.total)} of{" "}
                  {userBalancesData.total}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 hover:text-white disabled:opacity-50"
                    type="button"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={(page + 1) * limit >= userBalancesData.total}
                    className="px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 hover:text-white disabled:opacity-50"
                    type="button"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Withdrawals tab */}
        {activeTab === "withdrawals" && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-800">
              <h2 className="font-semibold text-white">Pending Withdrawal Requests</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-gray-800/50">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">
                      ID
                    </th>
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">
                      User
                    </th>
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">
                      Asset
                    </th>
                    <th className="text-right py-3 px-4 text-sm text-gray-400 font-medium">
                      Amount
                    </th>
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">
                      To Address
                    </th>
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">
                      Created At
                    </th>
                    <th className="text-center py-3 px-4 text-sm text-gray-400 font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loadingWithdrawals ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-gray-500">
                        <RefreshCw
                          size={24}
                          className="inline-block animate-spin mr-2"
                        />
                        Loading withdrawal requests...
                      </td>
                    </tr>
                  ) : withdrawalsData?.items?.length ? (
                    withdrawalsData.items.map((item, idx) => (
                      <tr
                        key={idx}
                        className="border-t border-gray-800/50 hover:bg-gray-800/20"
                      >
                        <td className="py-3 px-4 text-xs text-gray-500 font-mono">
                          {item.id?.substring(0, 8)}...
                        </td>
                        <td className="py-3 px-4 text-gray-300">
                          {item.user_id?.substring(0, 8)}...
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium text-white">
                            {item.asset}
                          </div>
                          <div className="text-xs text-gray-500">{item.network}</div>
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-white">
                          {format(item.amount, getDecimals(item.asset))}
                        </td>
                        <td className="py-3 px-4 text-xs font-mono text-gray-400 truncate max-w-[150px]">
                          {item.to_address}
                        </td>
                        <td className="py-3 px-4 text-xs text-gray-400">
                          {item.created_at
                            ? new Date(item.created_at).toLocaleString()
                            : "—"}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <button
                              onClick={() => openApproveModal(item)}
                              className="p-2 rounded-lg bg-green-900/50 hover:bg-green-800 border border-green-800 text-green-400"
                              title="Approve"
                              type="button"
                            >
                              <CheckCircle size={16} />
                            </button>
                            <button
                              onClick={() => openRejectModal(item)}
                              className="p-2 rounded-lg bg-red-900/50 hover:bg-red-800 border border-red-800 text-red-400"
                              title="Reject"
                              type="button"
                            >
                              <XCircle size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-gray-500">
                        No pending withdrawal requests
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Summary tab */}
        {activeTab === "summary" && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-800">
              <h2 className="font-semibold text-white">Balance Summary</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="bg-gray-800/50">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">
                      Asset
                    </th>
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">
                      Network
                    </th>
                    <th className="text-right py-3 px-4 text-sm text-gray-400 font-medium">
                      User Available
                    </th>
                    <th className="text-right py-3 px-4 text-sm text-gray-400 font-medium">
                      User Locked
                    </th>
                    <th className="text-right py-3 px-4 text-sm text-gray-400 font-medium">
                      User Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loadingSummary ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-gray-500">
                        <RefreshCw
                          size={24}
                          className="inline-block animate-spin mr-2"
                        />
                        Loading summary data...
                      </td>
                    </tr>
                  ) : summaryData?.balances?.length ? (
                    summaryData.balances.map((item, idx) => (
                      <tr
                        key={idx}
                        className="border-t border-gray-800/50 hover:bg-gray-800/20"
                      >
                        <td className="py-3 px-4 font-medium text-white">
                          {item.asset}
                        </td>
                        <td className="py-3 px-4 text-gray-300">{item.network}</td>
                        <td className="py-3 px-4 text-right font-mono text-white">
                          {format(item.totalUserAvailable, getDecimals(item.asset))}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-yellow-400">
                          {format(item.totalUserLocked, getDecimals(item.asset))}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-green-400">
                          {format(item.totalUserBalance, getDecimals(item.asset))}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-gray-500">
                        No summary data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Adjust Balance Modal */}
      <Modal
        title="Adjust User Balance"
        open={adjustModalOpen}
        onClose={() => {
          setAdjustModalOpen(false);
          setSelectedUser(null);
          setAdjustAmount("");
          setAdjustReason("");
        }}
      >
        {selectedUser && (
          <form onSubmit={handleAdjustBalance}>
            <div className="mb-4">
              <div className="text-sm text-gray-400 mb-1">User</div>
              <div className="text-white">{selectedUser.email}</div>
              <div className="text-xs text-gray-500">{selectedUser.userId}</div>
            </div>

            <div className="mb-4">
              <div className="text-sm text-gray-400 mb-1">Asset</div>
              <div className="text-white">
                {selectedUser.asset} ({selectedUser.network})
              </div>
            </div>

            <div className="mb-4">
              <div className="text-sm text-gray-400 mb-1">Current Balance</div>
              <div className="text-white">
                Available:{" "}
                {format(selectedUser.available, getDecimals(selectedUser.asset))}{" "}
                {selectedUser.asset}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-1">
                Adjustment Amount (+ to add, - to subtract)
              </label>
              <input
                type="text"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                placeholder="e.g., 1.5 or -0.5"
                className="w-full rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-1">
                Reason (required for audit)
              </label>
              <input
                type="text"
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                placeholder="e.g., Manual correction for trade #123"
                className="w-full rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                required
              />
            </div>

            {adjustBalanceMutation.isError && (
              <div className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-800 text-red-400 text-sm">
                {adjustBalanceMutation.error?.response?.data?.detail ||
                  "An error occurred"}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setAdjustModalOpen(false);
                  setSelectedUser(null);
                  setAdjustAmount("");
                  setAdjustReason("");
                }}
                className="px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={adjustBalanceMutation.isPending}
                className="px-4 py-2 rounded-lg bg-indigo-700 hover:bg-indigo-600 text-white disabled:opacity-50 flex items-center gap-2"
              >
                {adjustBalanceMutation.isPending ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Adjust Balance"
                )}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Approve Withdrawal Modal */}
      <Modal
        title="Approve Withdrawal"
        open={approveModalOpen}
        onClose={() => {
          setApproveModalOpen(false);
          setSelectedWithdrawal(null);
          setApproveNote("");
          setBroadcastNow(false);
        }}
      >
        {selectedWithdrawal && (
          <form onSubmit={handleApproveWithdrawal}>
            <div className="mb-4">
              <div className="text-sm text-gray-400 mb-1">Withdrawal ID</div>
              <div className="text-white font-mono text-sm">{selectedWithdrawal.id}</div>
            </div>

            <div className="mb-4">
              <div className="text-sm text-gray-400 mb-1">User</div>
              <div className="text-white font-mono text-sm">{selectedWithdrawal.user_id}</div>
            </div>

            <div className="mb-4">
              <div className="text-sm text-gray-400 mb-1">Amount</div>
              <div className="text-white">
                {format(selectedWithdrawal.amount, getDecimals(selectedWithdrawal.asset))}{" "}
                {selectedWithdrawal.asset}
              </div>
            </div>

            <div className="mb-4">
              <div className="text-sm text-gray-400 mb-1">Destination Address</div>
              <div className="text-white font-mono text-sm break-all">
                {selectedWithdrawal.to_address}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-1">
                Approval Note (optional)
              </label>
              <input
                type="text"
                value={approveNote}
                onChange={(e) => setApproveNote(e.target.value)}
                placeholder="Optional admin note"
                className="w-full rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-white"
              />
            </div>

            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={broadcastNow}
                  onChange={(e) => setBroadcastNow(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-300">
                  Broadcast immediately (skips processing queue)
                </span>
              </label>
            </div>

            {approveWithdrawalMutation.isError && (
              <div className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-800 text-red-400 text-sm">
                {approveWithdrawalMutation.error?.response?.data?.detail ||
                  "An error occurred"}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setApproveModalOpen(false);
                  setSelectedWithdrawal(null);
                  setApproveNote("");
                  setBroadcastNow(false);
                }}
                className="px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={approveWithdrawalMutation.isPending}
                className="px-4 py-2 rounded-lg bg-green-700 hover:bg-green-600 text-white disabled:opacity-50 flex items-center gap-2"
              >
                {approveWithdrawalMutation.isPending ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Approve Withdrawal"
                )}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Reject Withdrawal Modal */}
      <Modal
        title="Reject Withdrawal"
        open={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false);
          setSelectedWithdrawal(null);
          setRejectReason("");
        }}
      >
        {selectedWithdrawal && (
          <form onSubmit={handleRejectWithdrawal}>
            <div className="mb-4">
              <div className="text-sm text-gray-400 mb-1">Withdrawal ID</div>
              <div className="text-white font-mono text-sm">{selectedWithdrawal.id}</div>
            </div>

            <div className="mb-4">
              <div className="text-sm text-gray-400 mb-1">Amount</div>
              <div className="text-white">
                {format(selectedWithdrawal.amount, getDecimals(selectedWithdrawal.asset))}{" "}
                {selectedWithdrawal.asset}
              </div>
            </div>

            <div className="mb-4">
              <div className="text-sm text-gray-400 mb-1">User</div>
              <div className="text-white font-mono text-sm">{selectedWithdrawal.user_id}</div>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-1">
                Reason for Rejection (required)
              </label>
              <input
                type="text"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g., Invalid address, insufficient funds"
                className="w-full rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                required
              />
            </div>

            {rejectWithdrawalMutation.isError && (
              <div className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-800 text-red-400 text-sm">
                {rejectWithdrawalMutation.error?.response?.data?.detail ||
                  "An error occurred"}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRejectModalOpen(false);
                  setSelectedWithdrawal(null);
                  setRejectReason("");
                }}
                className="px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={rejectWithdrawalMutation.isPending}
                className="px-4 py-2 rounded-lg bg-red-700 hover:bg-red-600 text-white disabled:opacity-50 flex items-center gap-2"
              >
                {rejectWithdrawalMutation.isPending ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Reject Withdrawal"
                )}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

export default TreasuryDashboard;
